import * as React from "react"
import {
  getAnalyticsConsent,
  hasAnalyticsConfiguration,
  installConversionTracking,
  loadAnalytics,
  setAnalyticsConsent,
} from "../utils/analytics"

const NOTICE_STORAGE_KEY = "lxl_privacy_notice_seen"
const AUTO_MINIMIZE_MS = 8000

const hasSeenNotice = () => {
  try {
    return window.localStorage.getItem(NOTICE_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

const rememberNotice = () => {
  try {
    window.localStorage.setItem(NOTICE_STORAGE_KEY, "true")
  } catch {
    // The notice can still minimize for the current page.
  }
}

const AnalyticsConsent = () => {
  const [consent, setConsent] = React.useState("granted")
  const [isOpen, setIsOpen] = React.useState(false)
  const [isInteracting, setIsInteracting] = React.useState(false)
  const closeButtonRef = React.useRef(null)
  const minimizedButtonRef = React.useRef(null)
  const shouldReturnFocus = React.useRef(false)

  React.useEffect(() => {
    const savedConsent = getAnalyticsConsent()
    setConsent(savedConsent)
    setIsOpen(!hasSeenNotice())

    if (savedConsent === "granted") {
      loadAnalytics()
    }

    return installConversionTracking()
  }, [])

  React.useEffect(() => {
    if (!isOpen || isInteracting) return undefined

    const timeoutId = window.setTimeout(() => {
      rememberNotice()
      setIsOpen(false)
    }, AUTO_MINIMIZE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isInteracting, isOpen])

  React.useEffect(() => {
    if (!isOpen && shouldReturnFocus.current) {
      shouldReturnFocus.current = false
      minimizedButtonRef.current?.focus()
    }
  }, [isOpen])

  if (!hasAnalyticsConfiguration()) return null

  const updateConsent = value => {
    setAnalyticsConsent(value)
    setConsent(value)
  }

  const minimizeNotice = () => {
    rememberNotice()
    shouldReturnFocus.current = true
    setIsOpen(false)
  }

  const openNotice = () => {
    setIsOpen(true)
    window.requestAnimationFrame(() => closeButtonRef.current?.focus())
  }

  return (
    <>
      {isOpen ? (
        <section
          aria-label="Preferencias de analítica"
          className="fixed bottom-3 left-3 z-[100] max-w-[calc(100vw-1.5rem)] rounded-xl border border-white/20 bg-black/95 p-3 pr-10 text-white shadow-xl backdrop-blur sm:max-w-sm"
          onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setIsInteracting(false)
            }
          }}
          onFocus={() => setIsInteracting(true)}
          onMouseEnter={() => setIsInteracting(true)}
          onMouseLeave={event => {
            if (!event.currentTarget.contains(document.activeElement)) {
              setIsInteracting(false)
            }
          }}
        >
          <button
            ref={closeButtonRef}
            aria-label="Cerrar y minimizar preferencias"
            className="absolute right-1 top-1 grid h-10 w-10 place-items-center rounded-full text-xl leading-none text-white/70 hover:bg-white/10 hover:text-white"
            onClick={minimizeNotice}
            type="button"
          >
            ×
          </button>
          <p className="text-xs leading-relaxed text-white/80">
            <strong className="text-white">Analítica y privacidad.</strong>{" "}
            Medimos visitas y acciones para mejorar el sitio. Puedes
            desactivarlo ahora o después.{" "}
            <a className="underline hover:text-yellow" href="/privacidad/">
              Ver aviso
            </a>
            .
          </p>
          <div className="mt-2 flex items-center justify-between gap-4">
            <label className="flex min-h-10 cursor-pointer items-center gap-2 text-xs font-semibold">
              <input
                checked={consent === "granted"}
                className="h-4 w-4 accent-yellow"
                onChange={event =>
                  updateConsent(event.target.checked ? "granted" : "denied")
                }
                type="checkbox"
              />
              Analítica activa
            </label>
            <button
              className="min-h-10 rounded-full border border-white/40 px-4 py-2 text-xs font-semibold hover:border-yellow hover:text-yellow"
              onClick={minimizeNotice}
              type="button"
            >
              Listo
            </button>
          </div>
        </section>
      ) : (
        <button
          ref={minimizedButtonRef}
          aria-label="Cambiar preferencias de analítica"
          className="fixed bottom-3 left-3 z-[90] rounded-full border border-white/20 bg-black/75 px-3 py-1.5 text-[11px] text-white/80 shadow-md backdrop-blur hover:border-yellow hover:text-yellow"
          onClick={openNotice}
          type="button"
        >
          Privacidad
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {consent === "granted"
          ? "Analítica activada"
          : "Analítica desactivada"}
      </span>
    </>
  )
}

export default AnalyticsConsent
